"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../../Components/ThemeContext";

const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,165,0,0.05) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

export default function HowItWorks() {
  const { isDark } = useTheme();
  // ✅ FIX: removed unused activeStep state — was causing react-hooks/exhaustive-deps warning
  const [visibleSteps, setVisibleSteps] = useState([]);
  const stepRefs = useRef([]);

  const steps = [
    {
      id: 1,
      number: "01",
      title: "Discovery & Planning",
      subtitle: "Understanding Your Business Needs",
      description:
        "We analyze your business goals and create a clear roadmap for web, mobile, or AI solutions.",
      image: "/Images/discover.png",
    },
    {
      id: 2,
      number: "02",
      title: "Design & Development",
      subtitle: "Building Your Digital Solution",
      description:
        "Our team designs user-friendly interfaces and develops high-performance applications.",
      image: "/Images/design.png",
    },
    {
      id: 3,
      number: "03",
      title: "Testing & Optimization",
      subtitle: "Ensuring Quality & Performance",
      description:
        "We test and optimize every feature to deliver secure, fast, and reliable solutions.",
      image: "/Images/test.png",
    },
    {
      id: 4,
      number: "04",
      title: "Launch & Support",
      subtitle: "Deployment & Continuous Support",
      description:
        "We launch your product and provide ongoing support, updates, and maintenance.",
      image: "/Images/launch.png",
    },
  ];

  useEffect(() => {
    const refs = [...stepRefs.current];
    const observers = refs.map((ref, index) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleSteps((prev) =>
              !prev.includes(index) ? [...prev, index] : prev
            );
          }
        },
        { threshold: 0.5 }
      );
      if (ref) observer.observe(ref);
      return observer;
    });

    return () =>
      observers.forEach((observer, index) => {
        if (refs[index]) observer.unobserve(refs[index]);
      });
  }, []);

  const fadeUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  };

  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.2 } },
  };

  const cardBg = isDark ? "bg-gray-900" : "bg-[#1a2e1a]";
  const cardBorder = isDark ? "border-gray-800" : "border-green-900/40";
  const titleText = "text-white";
  const descText = "text-gray-300";
  const sectionGreenText = isDark ? "text-green-400" : "text-green-700";
  const greenText = "text-green-400";

  return (
    <motion.section
      aria-labelledby="how-it-works-heading"
      className={`relative w-full overflow-hidden py-10 px-4 sm:px-6 lg:px-8 ${
        isDark ? "bg-black text-white" : "bg-transparent text-gray-900"
      }`}
      style={isDark ? darkGridStyle : lightGridStyle}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={container}
    >
      <div className="max-w-6xl mx-auto">

        {/* SEO HEADING */}
        <motion.h2
          id="how-it-works-heading"
          className={`text-3xl sm:text-4xl font-bold text-center ${sectionGreenText} mb-12`}
          variants={fadeUp}
        >
          Our Process
        </motion.h2>

        <div className="max-w-4xl mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              ref={(el) => (stepRefs.current[index] = el)}
              className="flex gap-6 mb-8"
              variants={fadeUp}
              animate={visibleSteps.includes(index) ? "visible" : "hidden"}
            >
              {/* STEP NUMBER CIRCLE */}
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-bold bg-green-400 text-black border-green-400">
                  {step.number}
                </div>
              </div>

              {/* CARD */}
              <div className={`flex-1 ${cardBg} p-4 rounded-lg border ${cardBorder}`}>
                <div className="flex gap-4 items-center">

                  <img
                    src={step.image}
                    alt={`${step.title} process in web and mobile app development`}
                    className="w-12 h-12 object-contain"
                    loading="lazy"
                  />

                  <div>
                    <span className={`${greenText} text-sm`}>
                      Step {step.id}: {step.subtitle}
                    </span>

                    <h3 className={`text-lg font-bold ${titleText}`}>
                      {step.title}
                    </h3>

                    <p className={`${descText} text-sm`}>
                      {step.description}
                    </p>
                  </div>

                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}